import { Avatar, Skeleton } from "antd";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { DownArrowIcon } from "@/Components/Icons/DownArrowIcon";
import { POUND_SIGN } from "@/Constants/common";
import {
  deSelectAllListings,
  fetchApplicantLists,
  fetchSingleContractShift,
  manageExpandedRow,
  setLoading,
  updateApplicantListSorting,
  updateApplicantsPagination,
  updateSelectedApplication,
} from "@/reducers/clientcontractdetail";
import { getColumnNormalHtml } from "@/utilities/datatable/columnFunctions";
import { getComplianceClass } from "@/utilities/utils";

import { routingConstants } from "../../../../Constants/routingConstants";
import TableNoContent from "../../TableNoContent";
import { ContractsShifts } from "../candidates/Listing/common/ContractsShifts";
import { Miles } from "../candidates/Listing/common/Miles";
import BlurredText from "../managedService/BlurredText";

/**
 * Renders the Applicant Listing component.
 *
 * @return {JSX.Element} The rendered Applicant Listing component
 */
const ApplicantListing = () => {
  const {
    isApplicationLoading,
    contractApplicantsLists,
    contractDetails,
    selectedApplication,
    toggleSelected,
    contractId,
    applicantsPagination,
    filters,
    applicantListSorting,
  } = useSelector((state) => state.clientContractDetail);
  const { clientUserAllPermissions } = useSelector((state) => state.session);
  const [data, setData] = useState([]);
  const dispatch = useDispatch();
  const firstUpdateFromSearch = useRef(true);

  // console.log("contractApplicantsLists",contractApplicantsLists)
  useEffect(() => {
    // updating applicant/booked listing on search
    if (firstUpdateFromSearch.current) {
      firstUpdateFromSearch.current = false;
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      if (contractId == null) return;
      dispatch(
        updateApplicantsPagination({
          ...applicantsPagination,
          currentPage: 1,
          isLastPage: false,
        }),
      );
      dispatch(
        fetchApplicantLists({
          contract_id: contractId,
          search: filters.search,
        }),
      );
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [filters.search]);
  const columns = [
    {
      name: "Candidate",
      sortable: true,
      sortField: "candidate_forename",
      selector: (row) => (
        <div className="flex gap-[14px]">
          <div className="applicantListAvatar min-w-[45px]">
            <Avatar
              alt={"candidate"}
              sx={{ width: 50, height: 50 }}
              src={row.profile_image ? row.profile_image : ""}
            >
              {row.candidate_name
                ?.trim()
                .split(" ")[1] // Get the second word
                ?.charAt(0) // First character of the second word
                .toUpperCase()}
            </Avatar>
          </div>
          <div className="flex items-center justify-center">
            {(!clientUserAllPermissions ||
              clientUserAllPermissions.is_allow_to_view_candidate_profile ===
                1) &&
            row.is_past_booking ? (
              <>
                <Link
                  to={routingConstants.candidateDetail(row.route_id)}
                  className="truncate text-[14px] font-medium leading-[normal] text-black"
                >
                  {row.candidate_name}
                </Link>
              </>
            ) : (
              <span className="cursor-not-allowed truncate text-[14px] font-medium leading-[normal] text-black">
                {row.candidate_name}
              </span>
            )}

            {/* <Link
              to={routingConstants.candidateDetail(row.route_id)}
              className="truncate text-[14px] font-medium leading-[normal] text-black"
            >
              {row.candidate_name}
            </Link> */}
          </div>
        </div>
      ),
      width: "19%",
    },
    {
      name: "Contact",
      sortable: true,
      sortField: "candidate_email",
      selector: (row) => (
        <div className="flex flex-col">
          <BlurredText type="phone" blurKey={row.is_past_booking}>
            {getColumnNormalHtml(row.mobile_phone_number, "truncate")}
          </BlurredText>
          <BlurredText type="email" blurKey={row.is_past_booking}>
            <span className="truncate text-[12px] font-medium leading-[normal] text-txtColor">
              {row.candidate_email}
            </span>
          </BlurredText>
        </div>
      ),
      width: "14%",
    },
    ...(contractDetails?.contract_status !== "Worked" &&
    contractDetails?.contract_status !== "Booked"
      ? [
          {
            name: "Miles",
            selector: (row) => <Miles miles={row.miles} />,
            width: "95px",
          },
        ]
      : []),
    {
      name: "Compliance",
      sortable: true,
      sortField: "profile_percent",
      selector: (row) => (
        <span
          className={`inline-block h-[23px] w-[75px] !max-w-full rounded-[4px] text-center text-[14px] font-medium leading-[23px] ${getComplianceClass(
            row.profile_percent,
          )}`}
        >
          {row.profile_percent == "-" ? "0%" : row.profile_percent + "%"}
        </span>
      ),
      width: "9%",
    },
    {
      name: "Date Range",
      sortable: true,
      sortField: "contract_start_date",
      selector: (row) => (
        <div className="flex flex-col items-end">
          {getColumnNormalHtml(
            moment(row.contract_start_date).format("DD/MM/YYYY"),
          )}
          <span className="leading-[4px]">-</span>
          {getColumnNormalHtml(
            moment(row.contract_end_date).format("DD/MM/YYYY"),
          )}
        </div>
      ),
      width: "8%",
    },
    {
      name: "Role",
      sortable: true,
      sortField: "role_name",
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(
            row.role_name,
            "line-clamp-2 whitespace-break-spaces",
          )}
          <span className="line-clamp-1 text-[12px] font-medium leading-[normal] text-txtColor">
            {row.license_number}
          </span>
        </div>
      ),
      width: "9%",
    },
    // {
    //   name: "Actioned By",
    //   sortable: true,
    //   sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "actioned_by"),
    //   selector: (row) => (
    //     <div className="flex flex-col" data-tag="allowRowEvents">
    //       {
    //         getActionByHtml(row.actioned_by, row.actioned_at, '')
    //       }
    //     </div>
    //   ),
    //   width: "12%",
    // },
    {
      name: "Applied Date",
      sortable: true,
      sortField: "creted_at",
      selector: (row) =>
        getColumnNormalHtml(moment(row.apply_date).format("DD/MM/YYYY")),
      width: "9%",
    },
    {
      name: "Shift",
      sortable: true,
      sortField: "contract_start_time",
      selector: (row) => (
        <div className="flex flex-col items-end">
          {row.has_various_times ? (
            <span className="text-[14px] font-semibold leading-[normal] text-black">
              Various <br /> Times
            </span>
          ) : (
            <>
              {getColumnNormalHtml(
                moment(row.contract_start_time, "HH:mm:ss").format("HH:mm"),
              )}
              <span className="leading-[4px]">-</span>
              {getColumnNormalHtml(
                moment(row.contract_end_time, "HH:mm:ss").format("HH:mm"),
              )}
            </>
          )}
        </div>
      ),
      width: "5%",
    },
    {
      name: "Days",
      sortable: false,
      selector: (row) => (
        <div>{getColumnNormalHtml(row.total_days_of_contract)}</div>
      ),
      width: "4%",
    },
    {
      name: "Total",
      sortable: false,
      selector: (row) => (
        <div>
          {getColumnNormalHtml(
            `${POUND_SIGN} ` +
              Number(
                row.rate +
                  (row.cover_request_awarded_reward != 0
                    ? Number(row.cover_request_awarded_reward || 0)
                    : -Number(row.cover_request_awarded_penalty || 0)),
              ).toFixed(2),
          )}
        </div>
      ),
      width: "auto",
    },
    ...(!_.includes(["Worked", "Booked"], contractDetails.contract_status)
      ? [
          {
            name: " ",
            sortable: false,
            selector: (row) => (
              <div>
                <button
                  className={`transition-linear grid h-[24px] w-[24px] place-items-center bg-none text-txtColor ${
                    row.isExpanded ? "rotate-180 transform" : ""
                  }`}
                  onClick={() => {
                    if (!row.isExpanded) {
                      dispatch(
                        fetchSingleContractShift({
                          contract_id: row.contract_id,
                          candidate_id: row.candidate_id,
                        }),
                      );
                    }
                    dispatch(
                      manageExpandedRow({
                        candidate_id: row.candidate_id,
                        isExpanded: !row.isExpanded,
                      }),
                    );
                  }}
                >
                  <DownArrowIcon />
                </button>
              </div>
            ),
            width: "auto",
            center: true,
          },
        ]
      : [
          // {
          //   name: "Status",
          //   sortable: false,
          //   selector: (row) => (
          //     <WorkedStatusButton
          //       timesheet_status={contractDetails.contract_status}
          //       contract_ref_no={contractDetails.contract_ref_no}
          //       btnClass="h-[24px] cursor-pointer rounded-md border px-2 text-[14px] font-medium leading-[24px]"
          //     />
          //   ),
          //   width: "7%",
          // },
        ]),
  ];

  useEffect(() => {
    setData(contractApplicantsLists);
  }, [contractApplicantsLists]);
  /**
   * Renders an Expandedrow component with the given data.
   *
   * @param {object} data - the data for the Expandedrow component
   * @return {JSX.Element} the Expandedrow component
   */
  const Expandedrow = ({ data }) => (
    <ContractsShifts contract_id={data.contract_id} shifts={data.shifts} />
  );
  const dataTableRef = useRef();
  /**
   * A function that handles the scrolling of a table.
   *
   * @param {Event} e - The event object
   * @return {void}
   */
  const handleTableScroll = (e) => {
    let element = e.target;
    let scrollHeight = element.scrollHeight;
    let scrollTop = element.scrollTop;
    let clientHeight = element.clientHeight;

    if (scrollHeight - scrollTop - clientHeight < 2) {
      dispatch(fetchApplicantLists({}));
    }
  };
  useEffect(() => {
    if (dataTableRef.current) {
      var $table = dataTableRef.current.querySelector("._DataTable");
      $table.addEventListener("scroll", function (e) {
        handleTableScroll(e);
      });
    }
  }, []);

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = applicantListSorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateApplicantListSorting({
        sortByColumn: newSortField,
        sortByOrder: newSortOrder,
      }),
    );
    dispatch(deSelectAllListings());
    dispatch(
      updateApplicantsPagination({
        ...applicantsPagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(setLoading(true));
    dispatch(fetchApplicantLists({}));
  };

  return (
    <>
      <div ref={dataTableRef}>
        <DataTable
          columns={columns}
          data={data}
          onSort={handleSort}
          noDataComponent={<TableNoContent />}
          selectableRows
          progressPending={isApplicationLoading}
          progressComponent={<Skeleton active paragraph={{ rows: 1 }} />}
          fixedHeader
          fixedHeaderScrollHeight={"600px"}
          expandableRows
          // expandOnRowClicked
          className="_DataTable"
          clearSelectedRows={toggleSelected}
          expandableRowExpanded={(row) => row.isExpanded}
          expandableRowsComponent={Expandedrow}
          // selectableRowSelected={(row) => {
          //   return selectedApplication.includes(row.applicant_id);
          // }}
          onSelectedRowsChange={(state) => {
            let applicant_ids = state.selectedRows.map(
              (row) => row.applicant_id,
            );
            dispatch(updateSelectedApplication(applicant_ids));
          }}
        />
      </div>
    </>
  );
};

export default ApplicantListing;
